import { AxiosResponse } from "axios";
import { AxiosHelper } from "../axiosConfig";
import {
  IStandard,
  IUnAssociatedStandardRequest,
  StandardMagicMatchData,
} from "../model/interface/standard";
import { IStandardVideo } from "../model/interface/video";
import { ISelectElement } from "../model/interface/keyValuePair";
import APIConstant from "../utils/apiConstant";

export const getStandards = (standard: IStandard) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_STANDARD, {
    params: {
      pageSize: standard.pageSize,
      pageNumber: standard.pageNumber,
      name: standard.name,
      code: standard.code,
      description: standard.description,
      contentId: standard.contentAreaId,
      subjectId: standard.subjectId,
      domainId: standard.domainId,
      orderbyColumn: standard.orderbyColumn,
      sortDir: standard.sortDir,
      isActive: standard.activeInactive ? undefined : standard.isActive,
      standardIds: standard.standardId ? standard.standardId : "",
      isLinked: standard.all ? undefined : standard.isLinked,
    },
  });
};

export const getStandardOptionsByDomain = (
  domainId: number | string
): Promise<ISelectElement[]> =>
  getStandards({
    domainId: String(domainId),
    isActive: true,
    isApproved: true,
    pageSize: 999 * 999,
    pageNumber: 1,
    orderbyColumn: "name",
    sortDir: "asc",
  } as IStandard).then((response) =>
    (response.data?.data ?? []).map(
      (s: { standardId: number; name: string; code: string }) => ({
        value: String(s.standardId),
        label: s.code ? `[${s.code}] ${s.name}` : s.name,
      })
    )
  );

export const getAllStandards = (standard: IStandard) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_STANDARD, {
    params: {
      pageSize: standard.pageSize,
      pageNumber: standard.pageNumber,
      name: standard.name,
      code: standard.code,
      description: standard.description,
      contentId: standard.contentAreaId,
      subjectId: standard.subjectId,
      domainId: standard.domainId,
      orderbyColumn: standard.orderbyColumn,
      sortDir: standard.sortDir,
      isActive: standard.activeInactive ? undefined : standard.isActive,
      standardIds: standard.standardId ? standard.standardId : "",
      isLinked: standard.all ? undefined : standard.isLinked,
      gradeLevel: standard.gradeLevel,
      grades: standard.grades,
    },
  });
};

export const getStandardByIds = (standardIds: string) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_STANDARD, {
    params: {
      orderbyColumn: "name",
      sortDir: "asc",
      isActive: true,
      standardIds: standardIds,
    },
  });
};

export const getStandardsByDomainId = (domainId: number) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_STANDARD, {
    params: {
      domainId: domainId,
      isActive: true,
      isApproved: true,
    },
  });
};
export const getStandardsByWorksheetId = (
  worksheetId: number,
  pageSize: number,
  pageNumber: number,
  columnName: string,
  sortDir: string
) => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_STANDARD_BY_WORKSHEETID,
    {
      params: {
        worksheetId: worksheetId,
        pageSize: pageSize,
        pageNumber: pageNumber,
        orderbyColumn: columnName,
        sortDir: sortDir,
      },
    }
  );
};

export const getStandardsBySlideId = (
  slideId: number,
  pageSize: number,
  pageNumber: number,
  columnName: string,
  sortDir: string
) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_STANDARD_BY_SLIDEID, {
    params: {
      slideId: slideId,
      pageSize: pageSize,
      pageNumber: pageNumber,
      orderbyColumn: columnName,
      sortDir: sortDir,
    },
  });
};

export const getStandardsByDomain = (domainId) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_STANDARD, {
    params: {
      domainId: domainId,
      orderbyColumn: "name",
      sortDir: "asc",
      isActive: true,
      isApproved: true,
    },
  });
};

export const getStandardsByDomainAndSourceStandard = (
  domainId: number,
  sourceId: number,
  searchText: string,
  contentId?: number
) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_STANDARD, {
    params: {
      domainId: domainId,
      orderbyColumn: "name",
      sortDir: "asc",
      isActive: true,
      isApproved: true,
      SourceStandardId: sourceId,
      SearchText: searchText,
      ContentId: contentId,
    },
  });
};

export const getStandard = (id) => {
  return AxiosHelper.axiosInstance().get(
    `${APIConstant.CMS_STANDARD_DETAIL}/${id}`,
    {}
  );
};

export const addStandard = (standard: IStandard) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_STANDARD,
    standard,
    {}
  );
};

export const updateStandard = (standard: IStandard) => {
  return AxiosHelper.axiosInstance().put(
    APIConstant.CMS_STANDARD,
    standard,
    {}
  );
};

export const deleteStandard = (id) => {
  return AxiosHelper.axiosInstance().delete(APIConstant.CMS_STANDARD, {
    params: { id: id },
  });
};
export const deleteStandardVideo = (standardVideo: IStandardVideo) => {
  return AxiosHelper.axiosInstance().delete(APIConstant.CMS_STANDARDVIDEO, {
    params: {
      standardId: standardVideo.standardId,
      videoid: standardVideo.videoId,
    },
  });
};
export const deleteStandardWorksheet = (
  standardId: number,
  worksheetId: number
) => {
  return AxiosHelper.axiosInstance().delete(APIConstant.CMS_STANDARWORKSHEET, {
    params: {
      standardId: standardId,
      worksheetId: worksheetId,
    },
  });
};

export const deleteStandardSlide = (standardId: number, slideId: number) => {
  return AxiosHelper.axiosInstance().delete(APIConstant.CMS_STANDARDSLIDE, {
    params: {
      standardId: standardId,
      slideId: slideId,
    },
  });
};

export const associateStandardQuestions = (params) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_ASSOCIATE_QUESTIONS,
    params,
    {}
  );
};

export const copyToDomain = (params) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_COPY_STANDARDS,
    params,
    {}
  );
};

export const uploadStandardIcon = (uploadStandardIcon) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_STANDARD_ICON,
    uploadStandardIcon,
    {}
  );
};

export const getStandardsByQuestionId = (
  questionId: number,
  pageSize: number,
  pageNumber: number,
  columnName: string,
  sortDir: string,
  includeUnApprovedSubject?: boolean
) => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_STANDARD_BY_QUESTIONID,
    {
      params: {
        questionId: questionId,
        pageSize: pageSize,
        pageNumber: pageNumber,
        orderbyColumn: columnName,
        sortDir: sortDir,
        IncludeUnApprovedSubject: includeUnApprovedSubject,
      },
    }
  );
};

export const associateWorksheetStandards = (
  workSheetId: number,
  standardIds: number[]
) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_ASSOCIATE_WORKSHEETS,
    {
      WorkSheetId: workSheetId,
      StandardIds: standardIds,
    }
  );
};

export const associateStandardWorksheets = (
  standardId: number,
  worksheetIds: number[]
) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_ASSOCIATE_STANDARD_WORKSHEETS,
    {
      StandardId: standardId,
      WorksheetIds: worksheetIds,
    }
  );
};

export const associateVideoStandards = (
  videoId: number,
  standardIds: number[]
) => {
  return AxiosHelper.axiosInstance().post(APIConstant.CMS_ASSOCIATE_VIDEOS, {
    VideoId: videoId,
    StandardIds: standardIds,
  });
};

export const associateStandardVideos = (
  standardId: number,
  videoIds: number[]
) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_ASSOCIATE_STANDARD_VIDEOS,
    {
      StandardId: standardId,
      VideoIds: videoIds,
    }
  );
};

export const associateStandardSlides = (
  standardId: number,
  slideIds: number[]
) => {
  return AxiosHelper.axiosInstance().post(
    APIConstant.CMS_ASSOCIATE_STANDARD_SLIDES,
    {
      StandardId: standardId,
      SlideIds: slideIds,
    }
  );
};

export const associateSlideStandards = (
  slideId: number,
  standardIds: number[]
) => {
  return AxiosHelper.axiosInstance().post(APIConstant.CMS_ASSOCIATE_SLIDES, {
    SlideId: slideId,
    StandardIds: standardIds,
  });
};

export const validateStandardQuestionAssociation = (
  questionIds,
  standardId
) => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_VALIDATE_ASSOCIATE_QUESTION,
    {
      params: {
        questionIds: questionIds,
        standardId: standardId,
      },
    }
  );
};

export const validateStandardWorksheetAssociation = (
  worksheetIds,
  standardId
) => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_VALIDATE_ASSOCIATE_WORKSHEET,
    {
      params: {
        worksheetIds: worksheetIds,
        standardId: standardId,
      },
    }
  );
};

export const validateStandardVideoAssociation = (videoIds, standardId) => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_VALIDATE_ASSOCIATE_VIDEO,
    {
      params: {
        videoIds: videoIds,
        standardId: standardId,
      },
    }
  );
};

export const getUnAssociatedStandards = (
  request: IUnAssociatedStandardRequest
) => {
  return AxiosHelper.axiosInstance().get(
    APIConstant.CMS_UNASSOCIATED_STANDARD,
    {
      params: {
        questionId: request.questionId,
        videoId: request.videoId,
        worksheetId: request.worksheetId,
        slideId: request.slideId,
        contentAreaId: request.contentAreaId,
        isActive: request.isActive,
        gradeLevel: request.gradeLevel,
        grades: request.grades,
        description: request.description,
        pageSize: request.pageSize,
        pageNumber: request.pageNumber,
        orderByColumn: request.orderByColumn,
        sortDir: request.sortDir,
        standardIds: request.standardIds,
      },
    }
  );
};

export const getStandardMagicMatch = (standardId: number) => {
  return AxiosHelper.axiosInstance().get(APIConstant.CMS_STANDARD_MAGIC_MATCH, {
    params: {
      standardId: standardId,
    },
  });
};

export const getStandardMagicMatchList = (
  standardId: number
): Promise<AxiosResponse<StandardMagicMatchData[]>> => {
  return AxiosHelper.axiosInstance().get<StandardMagicMatchData[]>(
    APIConstant.CMS_STANDARD_MAGIC_MATCH,
    {
      params: {
        standardId: standardId,
      },
    }
  );
};
